import { chunk, uniq } from "es-toolkit";
import { action, computed, makeObservable, reaction } from "mobx";

import { BaseNodeModel } from "@/models/nodes/BaseNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";

import type { ActiveCursorModel } from "@/models/CursorModel";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel, NodeId } from "@/models/nodes/types";
import type { VisitableNode, NodeVisitor } from "@/models/nodes/visitors/NodeVisitor";
import type {
  MatrixNodeProps,
  MatrixNodeConstructorProps,
  MatrixBounds,
  CellPosition,
} from "@corca/data";

export class MatrixNodeModel extends BaseNodeModel<MatrixNodeProps> implements VisitableNode {
  static type = "matrix" as const;

  type = MatrixNodeModel.type;

  static adjustBounds(nodes: NodeModel[], bounds: MatrixBounds): MatrixBounds {
    if (nodes.length > bounds.cols * bounds.rows) {
      return {
        ...bounds,
        rows: Math.ceil(nodes.length / bounds.cols),
      };
    }

    return bounds;
  }

  static prefillMatrix(doc: DocumentModel, nodes: NodeModel[], bounds: MatrixBounds): NodeModel[] {
    const fillerCells = Math.max(0, bounds.cols * bounds.rows - nodes.length);

    return [
      ...nodes,
      ...Array.from({ length: fillerCells }).map(() => doc.create(InlineNodeModel, [])),
    ];
  }

  protected fillRequiredProps(props: MatrixNodeConstructorProps): MatrixNodeProps {
    this.reportMissingProps(props, ["rows", "cols"]);
    return { ...props, rows: props.rows ?? 1, cols: props.cols ?? 1 };
  }

  static create(
    doc: DocumentModel,
    { rows, cols, ...props }: MatrixNodeConstructorProps = { rows: 1, cols: 1 },
    nodes: InlineNodeModel[] = [],
    id?: NodeId,
  ) {
    const bounds = MatrixNodeModel.adjustBounds(nodes, { rows, cols });
    const matrixNodes = MatrixNodeModel.prefillMatrix(doc, nodes, bounds);

    return new MatrixNodeModel(doc, id, { ...bounds, ...props }, matrixNodes);
  }

  constructor(...args: ConstructorParameters<typeof BaseNodeModel<MatrixNodeConstructorProps>>) {
    super(...args);
    makeObservable(this, {
      matrix: computed,
      matrixNodes: true,
      insertRow: action,
      appendRow: action,
      removeRow: action,
      insertColumn: action,
      appendColumn: action,
      removeColumn: action,
      moveRow: action,
      moveColumn: action,
      setBounds: action,
    });

    this.onCleanup(
      reaction(
        () => this.bounds,
        (bounds, prevBounds) => {
          if (!prevBounds) {
            return;
          }

          if (bounds.rows !== prevBounds.rows || bounds.cols !== prevBounds.cols) {
            this.onMatrixBoundsChanged(bounds, prevBounds);
          }
        },
        { fireImmediately: false },
      ),
    );
  }

  private onMatrixBoundsChanged(bounds: MatrixBounds, prevBounds: MatrixBounds): void {
    if (bounds.cols < prevBounds.cols) {
      if (this.augmentedBarIndices.length === 0) {
        return;
      }

      this.augmentedBarIndices = uniq(
        this.augmentedBarIndices.filter((index) => bounds.cols - 1 > index),
      );
    }
  }

  get rows(): number {
    return this.props.rows;
  }

  private set rows(rows: number) {
    this.updateProps({ rows });
  }

  get cols(): number {
    return this.props.cols;
  }

  private set cols(cols: number) {
    this.updateProps({ cols });
  }

  get bounds(): MatrixBounds {
    return {
      rows: this.rows,
      cols: this.cols,
    };
  }

  private set bounds({ rows, cols }) {
    this.rows = rows;
    this.cols = cols;
  }

  get matrixNodes() {
    return this.nodes.filter((n) => n.is(InlineNodeModel));
  }

  get matrix(): InlineNodeModel[][] {
    return chunk(this.matrixNodes, this.bounds.cols ?? 1);
  }

  insertRow(insertAt: number) {
    const bounds = {
      ...this.bounds,
      rows: this.bounds.rows + 1,
    };
    const { matrix: currentMatrix } = this;

    const row = Array.from({ length: bounds.cols }).map(() => this.doc.create(InlineNodeModel, []));
    const newMatrix: InlineNodeModel[][] = [
      ...currentMatrix.slice(0, insertAt),
      row,
      ...currentMatrix.slice(insertAt),
    ];

    this.setChildren(newMatrix.flat());
    this.bounds = bounds;
  }

  appendRow() {
    this.insertRow(this.bounds.rows);
  }

  removeRow(from?: number): boolean {
    const bounds = {
      ...this.bounds,
      rows: this.bounds.rows - 1,
    };

    if (bounds.rows === 0) {
      return false;
    }

    if (typeof from === "number") {
      const nodes = this.matrix[from] || [];

      nodes.forEach((node) => {
        this.doc.removeNode(node);
      });
      this.bounds = bounds;
    } else {
      this.setBounds(bounds);
    }

    return true;
  }

  insertColumn(insertAt: number) {
    const bounds = {
      ...this.bounds,
      cols: this.bounds.cols + 1,
    };
    const { matrix: currentMatrix } = this;

    const newMatrix: InlineNodeModel[][] = [];

    currentMatrix.forEach((row, rowIndex) => {
      newMatrix[rowIndex] = [
        ...row.slice(0, insertAt),
        this.doc.create(InlineNodeModel, []),
        ...row.slice(insertAt),
      ];
    });

    this.setChildren(newMatrix.flat());
    this.bounds = bounds;

    this.augmentedBarIndices = this.augmentedBarIndices.map((index) =>
      index >= insertAt - 1 ? index + 1 : index,
    );
  }

  appendColumn() {
    this.insertColumn(this.bounds.cols);
  }

  removeColumn(from?: number): boolean {
    const bounds = {
      ...this.bounds,
      cols: this.bounds.cols - 1,
    };

    if (bounds.cols === 0) {
      return false;
    }

    if (typeof from === "number") {
      const nodes = this.getColumnNodes(from);

      nodes.forEach((node) => {
        this.doc.removeNode(node);
      });
      this.removeAugmentedBar(from);
      this.bounds = bounds;
    } else {
      this.setBounds(bounds);
    }

    this.augmentedBarIndices = this.augmentedBarIndices.map((index) =>
      index >= (from ?? bounds.cols) ? index - 1 : index,
    );

    return true;
  }

  moveRow(from: number, to: number) {
    const rows = [...this.matrix];
    rows.splice(to, 0, ...rows.splice(from, 1));
    this.setReorderedChildren(rows.flat());
  }

  moveColumn(from: number, to: number) {
    const rows = this.matrix.map((row) => {
      const cells = [...row];
      cells.splice(to, 0, ...cells.splice(from, 1));
      return cells;
    });
    this.setReorderedChildren(rows.flat());
  }

  // setChildren only syncs added and removed children, not a new order of the same ones.
  private setReorderedChildren(children: InlineNodeModel[]) {
    this.setChildren(children);
    this.prepareUpdate();
  }

  setBounds(bounds: MatrixBounds) {
    const { matrix: currentMatrix } = this;

    const newMatrix: NodeModel[][] = [];

    for (let row = 0; row < bounds.rows; row++) {
      newMatrix[row] = [];

      for (let col = 0; col < bounds.cols; col++) {
        if (row < currentMatrix.length && col < currentMatrix[row].length) {
          newMatrix[row][col] = currentMatrix[row][col];
        } else {
          newMatrix[row][col] = this.doc.create(InlineNodeModel, []);
        }
      }
    }

    this.setChildren(newMatrix.flat());
    this.bounds = bounds;
  }

  getColumnNodes(col: number) {
    return this.matrix.reduce<InlineNodeModel[]>((acc, row) => {
      return [...acc, row[col]];
    }, []);
  }

  getCellPosition(node?: InlineNodeModel): CellPosition | undefined {
    const nodeIndex = node ? this.matrixNodes.indexOf(node) : -1;

    if (nodeIndex === -1) {
      return undefined;
    }

    const row = Math.floor(nodeIndex / this.bounds.cols);
    const col = nodeIndex % this.bounds.cols;

    return { row, col };
  }

  getCellNode(node?: InlineNodeModel): InlineNodeModel | undefined {
    const position = this.getCellPosition(node);
    return this.getCellNodeByPosition(position);
  }

  getCellNodeByPosition(position?: CellPosition): InlineNodeModel | undefined {
    if (position) {
      const { row, col } = position;
      return this.matrix[row]?.[col];
    }

    return undefined;
  }

  get augmentedBarIndices() {
    return this.props.augmentedBarIndices ?? [];
  }

  set augmentedBarIndices(indices: number[]) {
    this.updateProps({ augmentedBarIndices: [...indices].sort((a, b) => a - b) });
  }

  addAugmentedBar(index: number) {
    if (!this.augmentedBarIndices.includes(index)) {
      this.augmentedBarIndices = [...this.augmentedBarIndices, index];
    }
  }

  removeAugmentedBar(index: number) {
    this.augmentedBarIndices = this.augmentedBarIndices.filter((i) => i !== index);
  }

  accept<R>(visitor: NodeVisitor<R>): R {
    return visitor.visitMatrixNode(this);
  }
}

export type MatrixCell = InlineNodeModel & {
  parent: MatrixNodeModel;
};

export type ActiveMatrixCursor = ActiveCursorModel & {
  node: MatrixCell;
};
